import axios from "axios";
import api from "@/components/api.js";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import i18n from "@/locales/index.js";
import { db } from "@/firebase";
import {
    collection,
    query,
    orderBy,
    limit,
    getDocs,
    onSnapshot,
    startAfter,
} from "firebase/firestore";

const state = () => ({
    chatMessage: "", // A message to be sent programmatically to chat
    replyToMessage: null, // The messgae to be replied to
    sendMessageLoading: false,
    messages: [],
    oldestMessage: null,
    showRecordSection: false,
});

const mutations = {
    setChatMessage(state, chatMessage) {
        state.chatMessage = chatMessage;
    },
    setReplyToMessage(state, message) {
        state.replyToMessage = message;
    },
    clearReplyToMessage(state) {
        state.replyToMessage = null;
    },
    setSendMessageLoading(state, loading) {
        state.sendMessageLoading = loading;
    },
    setMessages(state, messages) {
        state.messages = [...messages];
    },
    addMessage(state, message) {
        state.messages = [...state.messages, message];
    },
    clearMessages(state) {
        state.messages = [];
    },
    updateMessage(state, message) {
        const copyMessages = state.messages.map((doc) => {
            if (doc.id == message.id) return message;
            return doc;
        });
        state.messages = copyMessages;
    },
    setOldestMessage(state, messages) {
        state.oldestMessage = messages;
    },
    setShowRecordSection(state, value) {
        state.showRecordSection = !!value;
    },
};

const actions = {
    setChatMessage({ commit }, payload) {
        commit("setChatMessage", payload.chatMessage);
    },
    setReplyToMessage({ commit }, payload) {
        commit("setReplyToMessage", payload.message);
    },
    clearReplyToMessage({ commit }) {
        commit("clearReplyToMessage");
    },
    setSendMessageLoading({ commit }, payload) {
        commit("setSendMessageLoading", payload.loading);
    },

    async authenticateWithFirebase({ dispatch }) {
        // if (!window) return;
        const baseUrl = window.location.origin;
        try {
            // get firestore token
            const response = await axios.get(`${baseUrl}${api.auth.getMe}`);
            const { data } = response;
            const { result } = data;
            const { firestore_token } = result;
            const auth = getAuth();
            // authenticate with the new token
            await signInWithCustomToken(auth, firestore_token);
        } catch (error) {
            console.log("signInWithCustomToken", error);
            /* dispatch(
                "Toasts/addToast",
                {
                    message: i18n.global.t("Firebase Conversations Error"),
                    type: "error",
                },
                { root: true }
            ); */
        }
    },
    async sendMessage({ commit, dispatch }, payload) {
        try {
            commit("setSendMessageLoading", true);
            await axios.post(`${api.chat.sendMessage}`, payload.data);
        } catch (error) {
            dispatch(
                "Toasts/addToast",
                { message: i18n.global.t("Send Message Error"), type: "error" },
                { root: true }
            );
        } finally {
            commit("setSendMessageLoading", false);
        }
    },
    async fetchInitialMessages({ commit }, payload) {
        try {
            // clear last chat messages
            commit("clearMessages");
            // clear old message
            commit("setOldestMessage", null);
            const messagesQuery = query(
                collection(
                    db,
                    `tenants/${payload.tenantId}/conversations/${payload.id}/messages`
                ),
                orderBy("timestamp", "desc"),
                limit(10)
            );

            const querySnapshot = await getDocs(messagesQuery);
            const messages = querySnapshot.docs
                .map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }))
                .reverse();
            commit("setMessages", messages);
            commit(
                "setOldestMessage",
                querySnapshot.docs[querySnapshot.docs.length - 1]
            );
        } catch (error) {
            console.error("Error fetching latest messages: ", error);
        }
    },
    async listenForNewMessages({ commit, state }, payload) {
        let isListenerInitialized = false;
        const messagesQuery = query(
            collection(
                db,
                `tenants/${payload.tenantId}/conversations/${payload.id}/messages`
            ),
            orderBy("timestamp", "desc")
        );

        const unsubscribe = onSnapshot(messagesQuery, (querySnapshot) => {
            if (!isListenerInitialized) {
                isListenerInitialized = true;
            } else {
                querySnapshot
                    .docChanges()
                    .reverse()
                    .forEach((change) => {
                        if (change.type === "added") {
                            // This is a new document, so we process it
                            const message = {
                                id: change.doc.id,
                                ...change.doc.data(),
                            };
                            const messageIndex = state.messages.findIndex(
                                (doc) => doc.id == message.id
                            );
                            if (messageIndex == -1) {
                                commit("addMessage", message);
                            }
                        }
                        else if (change.type === "modified") {
                            const message = {
                                id: change.doc.id,
                                ...change.doc.data(),
                            };
                            const messageIndex = state.messages.findIndex(
                                (doc) => doc.id == message.id
                            );
                            if (messageIndex !== -1) {
                                commit("updateMessage", message);
                            }
                        }
                    });
            }
        });

        return unsubscribe;
    },
    async fetchMoreMessages({ commit, state }, payload) {
        try {
            // Check if there's a lastVisible document to start from
            if (!state.oldestMessage) return;

            const messagesQuery = query(
                collection(
                    db,
                    `tenants/${payload.tenantId}/conversations/${payload.id}/messages`
                ),
                orderBy("timestamp", "desc"),
                startAfter(state.oldestMessage),
                limit(10)
            );
            const querySnapshot = await getDocs(messagesQuery);
            const messages = querySnapshot.docs
                .map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }))
                .reverse();

            commit("setMessages", [...messages, ...state.messages]);

            // Update oldestMessage for next pagination
            commit(
                "setOldestMessage",
                querySnapshot.docs[querySnapshot.docs.length - 1]
            );
        } catch (error) {
            console.error("Error fetching more messages: ", error);
        }
    },
    setMessages({ commit }, messages) {
        commit("setMessages", messages);
    },
    addMessage({ commit }, message) {
        commit("addMessage", message);
    },
    clearMessages({ commit }) {
        commit("clearMessages");
    },
    setOldestMessage({ commit }, payload) {
        commit("setOldestMessage", payload.message);
    },
    setShowRecordSection({ commit }, payload) {
        commit("setShowRecordSection", payload);
    },
};

// Getters for the product module
const getters = {
    chatMessage(state) {
        return state.chatMessage;
    },
    replyToMessage(state) {
        return state.replyToMessage;
    },
    sendMessageLoading(state) {
        return state.sendMessageLoading;
    },
    messages(state) {
        return state.messages;
    },
    showRecordSection(state) {
        return state.showRecordSection;
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
